import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { loadPdfJs } from '@fias/pdf-core/loader';
import { captureError } from '@/lib/telemetry';

/**
 * Per-axis ceiling on a canvas backing store in mobile Safari. Exceed it and
 * the canvas is not an error — it is BLANK, which is indistinguishable from a
 * document that failed to render.
 */
const MAX_CANVAS_DIMENSION = 4096;

/** Total-pixel ceiling, the same limit expressed as an area (4096²). */
const MAX_CANVAS_AREA = 16_777_216;

/**
 * How many device pixels to allocate per CSS pixel.
 *
 * `devicePixelRatio`, reduced until the backing store fits inside what mobile
 * Safari will actually allocate. An iPhone reports a DPR of 3, so a page
 * rendered at any generous width crosses the per-axis ceiling long before a
 * desktop at DPR 1 or 2 comes near it — and the failure is a silently blank
 * canvas rather than a thrown error, so nothing in the render path notices.
 *
 * Exported for tests: the arithmetic is the whole defence, and it cannot be
 * exercised through a render in a DOM that has no real canvas.
 */
export function resolveCanvasBackingScale(
  cssWidth: number,
  cssHeight: number,
  devicePixelRatio: number,
): number {
  const dpr = devicePixelRatio > 0 ? devicePixelRatio : 1;
  if (cssWidth <= 0 || cssHeight <= 0) return dpr;
  const byAxis = Math.min(MAX_CANVAS_DIMENSION / cssWidth, MAX_CANVAS_DIMENSION / cssHeight);
  const byArea = Math.sqrt(MAX_CANVAS_AREA / (cssWidth * cssHeight));
  // No floor of 1: a page whose CSS size alone exceeds the ceiling has to be
  // rendered at less than one device pixel per CSS pixel. Blurry beats blank.
  return Math.min(dpr, byAxis, byArea);
}

/**
 * Strip the query string from every URL in a message.
 *
 * pdf.js reports a failed fetch as `Unexpected server response (403) while
 * retrieving PDF "<full url>"`, and the full URL of a signing document is a
 * presigned S3 link: its query string IS the credential. That message goes to
 * telemetry, into React state and, behind `?diag=1`, onto the screen — none of
 * which should hold a live document-access token until it expires.
 *
 * Exported for tests.
 */
export function redactUrlQueryStrings(message: string): string {
  return message.replace(/(https?:\/\/[^\s"'<>)]*?)\?[^\s"'<>)]*/g, '$1?…');
}

/** An `Error` safe to report: same name, message and stack with URL queries removed. */
function sanitizeError(err: unknown): Error {
  const source = err instanceof Error ? err : new Error(String(err));
  const clean = new Error(redactUrlQueryStrings(source.message));
  clean.name = source.name;
  clean.stack = source.stack ? redactUrlQueryStrings(source.stack) : undefined;
  return clean;
}

interface PdfPageViewProps {
  /** The fully-loaded pdfjs document handle (one per source PDF). */
  pdfDoc: import('pdfjs-dist').PDFDocumentProxy | null;
  /** 1-indexed page number to render. */
  pageNumber: number;
  /** Width in CSS pixels at which to render the page. Height is derived from
   *  the page's intrinsic aspect ratio. */
  width: number;
  /** Children rendered in a positioned overlay above the canvas, used for
   *  field placement. The overlay tracks the canvas's rendered size 1:1. */
  children?: (renderedSize: { width: number; height: number }) => ReactNode;
  /**
   * Stable address for this page, exposed as `data-pdf-page-key`.
   *
   * The signing page's field navigator needs to scroll to a page that has not
   * painted yet — its overlay children do not exist until `renderedSize` is
   * set, but this container always does, because it reserves height from the
   * start. Scrolling it into view is what triggers the paint.
   */
  pageKey?: string;
  /**
   * The owning document's fetch state, from `usePdfDocument`.
   *
   * Page containers do NOT wait for the document — their count comes from the
   * server's `pageCount`, so correctly-sized blank boxes appear immediately.
   * Without these, "still fetching" and "failed to paint" are the same
   * picture: a document-shaped hole with nothing to tell them apart.
   */
  documentLoading?: boolean;
  documentStalled?: boolean;
}

/**
 * Renders one PDF page to a canvas — lazily, via IntersectionObserver. A
 * 200-page document would be unusable if every page rendered eagerly, so we
 * defer the heavy `page.render()` work until the page scrolls within
 * `rootMargin` of the viewport.
 *
 * Re-renders happen if `width` changes (responsive resize) or `pageNumber`
 * changes. Each render cancels the previous in-flight one so a fast scroll
 * doesn't queue dozens of pending render tasks.
 */
export function PdfPageView({
  pdfDoc,
  pageNumber,
  width,
  children,
  pageKey,
  documentLoading = false,
  documentStalled = false,
}: PdfPageViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [visible, setVisible] = useState(false);
  const [renderedSize, setRenderedSize] = useState<{ width: number; height: number } | null>(null);
  /**
   * The failure is keyed to the document it happened on. A replacement
   * document (every session refetch mints a fresh presigned URL) gets a clean
   * slate without an effect that resets state: while `pdfDoc` is null between
   * documents the old failure is simply not the current one, so the loading
   * status can show instead of a stale error.
   */
  const [renderFailure, setRenderFailure] = useState<{
    doc: import('pdfjs-dist').PDFDocumentProxy;
    message: string;
  } | null>(null);
  const renderError = renderFailure && renderFailure.doc === pdfDoc ? renderFailure.message : null;
  /** `?diag=1` on the URL opts in to the (redacted) failure text. */
  const showDiagnostics =
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('diag');

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }

    // Stays CONNECTED, and tracks both directions.
    //
    // It used to disconnect on the first intersection, which made the first
    // paint the only paint. Mobile Safari discards canvas backing stores under
    // memory pressure — including when the tab is backgrounded, which is
    // exactly what the consent step's "open the document" button does — and a
    // page that can never re-render stays blank for the rest of the session.
    // Re-rendering on re-entry is what every virtualised PDF viewer does
    // anyway; the cost is a repaint the user is already waiting for.
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          setVisible(entry.isIntersecting);
        }
      },
      { rootMargin: '200px 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  /**
   * Bumped whenever the browser may have thrown the canvas contents away
   * without telling us: returning to a backgrounded tab, and a bfcache
   * restore. Included in the render effect's dependencies, so a page that is
   * still on screen repaints instead of sitting blank.
   */
  const [repaintToken, setRepaintToken] = useState(0);
  const bumpRepaint = useCallback(() => setRepaintToken((n) => n + 1), []);
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') bumpRepaint();
    };
    // `pageshow` fires on EVERY load, not only a bfcache restore — `persisted`
    // is what distinguishes them. Without the guard this bumped the token from
    // 0 to 1 immediately after the first paint, cancelling the in-flight
    // render task and re-rendering every visible page: double the initial work
    // on a multi-page document, and a visible flash while the canvas is
    // cleared and repainted.
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) bumpRepaint();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [bumpRepaint]);

  useEffect(() => {
    if (!visible || !pdfDoc || !canvasRef.current) return;

    let cancelled = false;
    let renderTask: import('pdfjs-dist').RenderTask | undefined;

    (async () => {
      try {
        await renderPage();
      } catch (err) {
        // `getPage` was previously OUTSIDE any catch, so a rejection here was
        // an unhandled promise rejection and the page simply stayed blank —
        // no error, no telemetry, nothing for anyone to act on. A signer
        // staring at an empty document is the least debuggable state this
        // surface has, so it now reports itself.
        if (cancelled) return;
        if (err instanceof Error && err.name === 'RenderingCancelledException') return;
        const clean = sanitizeError(err);
        setRenderFailure({ doc: pdfDoc!, message: clean.message });
        // `captureError`, not `traceError`: the latter takes a traceId from
        // `traceStart`, so an event NAME only records an in-memory entry
        // called "unknown" and never reaches /v1/telemetry/errors. This is
        // the one automatic signal this failure has.
        captureError(clean, {
          feature: 'document-sign',
          stage: 'pdf-page-render',
          pageNumber,
        });
      }
    })();

    async function renderPage() {
      const page = await pdfDoc!.getPage(pageNumber);
      if (cancelled) return;

      const baseViewport = page.getViewport({ scale: 1 });
      const scale = width / baseViewport.width;
      const viewport = page.getViewport({ scale });

      const canvas = canvasRef.current;
      if (!canvas) return;

      const dpr = resolveCanvasBackingScale(
        viewport.width,
        viewport.height,
        window.devicePixelRatio || 1,
      );
      canvas.width = Math.floor(viewport.width * dpr);
      canvas.height = Math.floor(viewport.height * dpr);
      canvas.style.width = `${Math.floor(viewport.width)}px`;
      canvas.style.height = `${Math.floor(viewport.height)}px`;

      const ctx = canvas.getContext('2d');
      // Null under memory pressure, which mobile Safari reaches long before a
      // desktop does. Silently returning left a grey box and no explanation.
      if (!ctx) throw new Error('Could not get a 2D drawing context for this page');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      renderTask = page.render({ canvasContext: ctx, viewport, canvas });
      await renderTask.promise;
      if (!cancelled) {
        setRenderFailure(null);
        setRenderedSize({ width: viewport.width, height: viewport.height });
      }
    }

    return () => {
      cancelled = true;
      try {
        renderTask?.cancel();
      } catch {
        // already settled
      }
    };
  }, [visible, pdfDoc, pageNumber, width, repaintToken]);

  return (
    <div
      ref={containerRef}
      className="relative inline-block bg-muted"
      style={{ width, minHeight: width * 1.3 }}
      data-testid={`pdf-page-${pageNumber}`}
      data-pdf-page-key={pageKey}
    >
      <canvas ref={canvasRef} className="block" />
      {/* A page that cannot paint has to SAY so. Blank paper on a signing
          surface is indistinguishable from a document with nothing on it,
          which is the one reading a signer must never be left with. */}
      {/* Shown whenever the document is (re)loading, NOT only before the first
          paint: a session refetch replaces the document after the page has
          rendered, and mobile Safari may have discarded that canvas in the
          meantime. The previous overlay children stay put so the fields do
          not flicker; the status simply sits on top of whatever is there. */}
      {!renderError && (documentLoading || documentStalled) && (
        <div className="absolute inset-0 flex items-center justify-center p-4">
          <p className="text-center text-xs text-muted-foreground" role="status">
            {documentStalled
              ? 'This document is taking longer than usual to open.'
              : 'Loading document…'}
          </p>
        </div>
      )}
      {renderError && (
        <div
          className="absolute inset-0 flex items-center justify-center p-4"
          role="alert"
          data-testid={`pdf-page-error-${pageNumber}`}
        >
          <div className="text-center">
            <p className="text-xs text-muted-foreground">
              This page could not be displayed here. Reload the page to try again.
            </p>
            {/* The reason, ONLY behind ?diag=1, and only after redaction.
                A phone has no console, so some channel has to carry it — but
                a pdf.js message has no contract about its contents, and an
                external recipient is the wrong audience for a worker path or
                an object identifier. Opt-in keeps it available to whoever is
                debugging and invisible to everyone signing. Anyone can add the
                flag, so it is not a security boundary: the credential-bearing
                part of a message is stripped BEFORE it reaches state, and the
                recipient already holds the URL the message would name. */}
            {showDiagnostics && (
              <p className="mt-1 break-words text-[10px] text-muted-foreground/70">{renderError}</p>
            )}
          </div>
        </div>
      )}
      {renderedSize && children ? (
        <div
          className="absolute inset-0"
          style={{ width: renderedSize.width, height: renderedSize.height }}
        >
          {children(renderedSize)}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Loads a pdfjs document from a URL and tracks its lifecycle. Returns null
 * while loading or if no URL is set; error surfaces via thrown promise so
 * callers can show their own UI state.
 */
export function usePdfDocument(url: string | null) {
  const [doc, setDoc] = useState<import('pdfjs-dist').PDFDocumentProxy | null>(null);
  const [error, setError] = useState<string | null>(null);
  /**
   * Loading is reported separately because the page containers do NOT wait for
   * it — their count comes from the server's `pageCount`, so correctly-sized
   * blank boxes appear immediately. That made "still fetching" and "failed to
   * paint" look exactly alike: a document-shaped hole, with no way for anyone
   * to tell which they were looking at.
   */
  const [loading, setLoading] = useState(false);
  const [stalled, setStalled] = useState(false);

  useEffect(() => {
    if (!url) {
      setDoc(null);
      setError(null);
      setLoading(false);
      setStalled(false);
      return;
    }
    setLoading(true);
    setStalled(false);
    // A load that never settles is otherwise indistinguishable from one that
    // failed. Say so rather than leaving the reader to guess, and report it,
    // because a PDF that will not fetch on one device and will on another is
    // not something anyone can debug from a screenshot.
    const stallTimer = setTimeout(() => {
      setStalled(true);
      captureError(new Error('PDF document did not load within 20s'), {
        feature: 'document-sign',
        stage: 'pdf-load-stalled',
        // Path only: a presigned query string is a credential.
        url: url.split('?')[0],
      });
    }, 20_000);

    // Drop the previous document BEFORE loading the next one.
    //
    // The cleanup below destroys the outgoing `loadingTask`, but this state
    // used to keep pointing at its now-DESTROYED proxy until the replacement
    // arrived. Any render in that window called `getPage()` on a dead document
    // and the page stayed blank.
    //
    // It is reachable in ordinary use, not a corner: every signing-session
    // refetch mints a fresh presigned URL, so `url` changes whenever the page
    // re-reads the session — which the § 7001(c)(1)(C)(ii) "open the document"
    // step does deliberately, to pick up that the retrieval was recorded.
    setDoc(null);
    let cancelled = false;
    let loadingTask: import('pdfjs-dist').PDFDocumentLoadingTask | undefined;
    (async () => {
      try {
        const pdfjs = await loadPdfJs();
        loadingTask = pdfjs.getDocument({ url });
        const loaded = await loadingTask.promise;
        if (cancelled) {
          // pdf.js 6 removed `PDFDocumentProxy.destroy()`; teardown is on the
          // loading task (which is what the effect cleanup below uses too).
          loaded.loadingTask.destroy().catch(() => {
            // already destroyed
          });
          return;
        }
        setDoc(loaded);
        setError(null);
        setLoading(false);
        setStalled(false);
        clearTimeout(stallTimer);
      } catch (err) {
        if (!cancelled) {
          const clean = sanitizeError(err);
          setError(clean.message || 'Failed to load PDF');
          setLoading(false);
          clearTimeout(stallTimer);
          captureError(clean, {
            feature: 'document-sign',
            stage: 'pdf-load-failed',
            url: url.split('?')[0],
          });
        }
      }
    })();
    return () => {
      cancelled = true;
      clearTimeout(stallTimer);
      if (loadingTask) {
        loadingTask.destroy().catch(() => {
          // already destroyed
        });
      }
    };
  }, [url]);

  return { doc, error, loading, stalled };
}
